import { useAudioBibleStore } from '../stores/audioBibleStore'
import { getBookById } from '../lib/bibleBooks'

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

export function BibleMiniPlayer() {
  const view = useAudioBibleStore((s) => s.view)
  const bookId = useAudioBibleStore((s) => s.bookId)
  const chapter = useAudioBibleStore((s) => s.chapter)
  const translation = useAudioBibleStore((s) => s.translation)
  const status = useAudioBibleStore((s) => s.status)
  const currentPositionSec = useAudioBibleStore((s) => s.currentPositionSec)
  const totalDurationSec = useAudioBibleStore((s) => s.totalDurationSec)
  const progress = useAudioBibleStore((s) => s.progress)
  const errorMessage = useAudioBibleStore((s) => s.errorMessage)

  const expand = useAudioBibleStore((s) => s.expand)
  const pause = useAudioBibleStore((s) => s.pause)
  const resume = useAudioBibleStore((s) => s.resume)
  const dismiss = useAudioBibleStore((s) => s.dismiss)

  if (view !== 'mini') return null
  if (!bookId || !chapter) return null

  const bookName = getBookById(bookId)?.name ?? bookId
  const progressPct = totalDurationSec > 0 ? (currentPositionSec / totalDurationSec) * 100 : 0

  let statusLine: string
  if (status === 'synthesizing') {
    statusLine = `Preparing audio… (${progress.done}/${progress.total})`
  } else if (status === 'loading') {
    statusLine = 'Loading…'
  } else if (status === 'error') {
    statusLine = errorMessage ?? 'Audio error'
  } else {
    statusLine = `${bookName} ${chapter} · ${translation} · ${formatTime(currentPositionSec)} / ${formatTime(totalDurationSec)}`
  }

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 w-full max-w-lg z-40 bg-elevated border-t border-surface px-3 py-2 flex items-center gap-3"
      style={{
        bottom: 'calc(56px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <button
        type="button"
        aria-label={status === 'playing' ? 'Pause' : 'Play'}
        onClick={(e) => {
          e.stopPropagation()
          if (status === 'playing') pause()
          else resume()
        }}
        className="w-9 h-9 rounded-full bg-accent text-surface flex items-center justify-center shrink-0"
      >
        {status === 'playing' ? (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        ) : (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>

      <button
        type="button"
        onClick={expand}
        className="flex-1 min-w-0 text-left"
        aria-label="Expand player"
      >
        <div className="text-text-primary text-sm truncate">{statusLine}</div>
        <div className="h-0.5 bg-surface rounded mt-1 overflow-hidden">
          <div
            className="h-full bg-accent transition-[width] duration-200"
            style={{ width: `${Math.min(progressPct, 100)}%` }}
          />
        </div>
      </button>

      <button
        type="button"
        onClick={expand}
        aria-label="Expand"
        className="text-text-muted text-xs px-1 shrink-0"
      >
        ▲
      </button>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          dismiss()
        }}
        aria-label="Close player"
        className="text-text-muted text-sm px-2 shrink-0 border-l border-surface"
      >
        ✕
      </button>
    </div>
  )
}
